import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

import { TFlatBlock } from './helpers/blocksToFlatDocument';

type TState = {
  collapsedBlockIds: string[];
  setCollapsedBlockIds: (v: string[]) => void;
};

const Context = createContext<TState | null>(null);

type Props = {
  children: React.ReactNode;
};
export function NavigatorStoreProvider({ children }: Props) {
  const [collapsedBlockIds, setCollapsedBlockIds] = useState<string[]>([]);
  const value = useMemo<TState>(
    () => ({ collapsedBlockIds, setCollapsedBlockIds }),
    [collapsedBlockIds, setCollapsedBlockIds]
  );
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

function useNavigatorContext() {
  const context = useContext(Context);
  if (!context) {
    throw new Error('useNavigatorContext must be used within a NavigatorStoreProvider');
  }
  return context;
}

export function useToggleCollapseBlockId() {
  const c = useNavigatorContext();
  return useCallback(
    (id: string) => {
      const { collapsedBlockIds, setCollapsedBlockIds } = c;
      if (collapsedBlockIds.includes(id)) {
        setCollapsedBlockIds(collapsedBlockIds.filter((bId) => bId !== id));
        return;
      }
      setCollapsedBlockIds([...collapsedBlockIds, id]);
    },
    [c]
  );
}

export function useBlockAncestorCollapsed(flatBlock: TFlatBlock) {
  const { collapsedBlockIds } = useNavigatorContext();
  return useMemo(() => {
    for (const parentId of flatBlock.parentIds) {
      if (collapsedBlockIds.includes(parentId)) {
        return true;
      }
    }
    return false;
  }, [flatBlock, collapsedBlockIds]);
}

export function useBlockCollapsed(flatBlock: TFlatBlock) {
  const { collapsedBlockIds } = useNavigatorContext();
  return useMemo(() => collapsedBlockIds.includes(flatBlock.id), [flatBlock, collapsedBlockIds]);
}
